// Office.js initialization and helpers

let officeInitialized = false;

export async function initializeOffice(): Promise<void> {
  if (officeInitialized) return;

  return new Promise((resolve, reject) => {
    Office.onReady((info) => {
      if (info.host === Office.HostType.Outlook) {
        officeInitialized = true;
        console.log('Office.js initialized for Outlook');
        resolve();
      } else {
        reject(new Error(`Unsupported Office host: ${info.host}`));
      }
    });
  });
}

export function isOfficeInitialized(): boolean {
  return officeInitialized;
}

// Get the current email item
export function getCurrentMailItem(): Office.MessageRead | null {
  if (!officeInitialized) return null;
  return Office.context.mailbox.item as Office.MessageRead | null;
}

// Get email body as HTML
export async function getEmailBody(): Promise<string> {
  const item = getCurrentMailItem();
  if (!item) throw new Error('No email selected');

  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Html, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || 'Failed to get email body'));
      }
    });
  });
}

// Get email body as plain text
export async function getEmailBodyText(): Promise<string> {
  const item = getCurrentMailItem();
  if (!item) throw new Error('No email selected');

  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || 'Failed to get email body'));
      }
    });
  });
}

// Get email subject
export function getEmailSubject(): string | undefined {
  const item = getCurrentMailItem();
  return item?.subject;
}

// Get sender information
export function getEmailSender(): { name: string; email: string } | null {
  const item = getCurrentMailItem();
  if (!item?.from) return null;

  return {
    name: item.from.displayName,
    email: item.from.emailAddress,
  };
}

// Get email ID (for Graph API calls)
export function getEmailItemId(): string | undefined {
  const item = getCurrentMailItem();
  return item?.itemId;
}

// Display a reply form with the draft content
export async function displayReplyWithDraft(htmlBody: string): Promise<void> {
  const item = getCurrentMailItem();
  if (!item) throw new Error('No email selected');

  return new Promise((resolve, reject) => {
    item.displayReplyForm({
      htmlBody: htmlBody,
      callback: (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || 'Failed to display reply form'));
        }
      },
    });
  });
}

// Show notification in Outlook
export async function showNotification(
  key: string,
  message: string,
  type: 'informational' | 'error' = 'informational'
): Promise<void> {
  const item = getCurrentMailItem();
  if (!item) return;

  return new Promise((resolve) => {
    item.notificationMessages.addAsync(
      key,
      {
        type:
          type === 'error'
            ? Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage
            : Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
        message: message,
        persistent: type === 'error',
      },
      () => resolve()
    );
  });
}
